import { useState } from "react";
import { Clock } from "lucide-react";
import BookingModal from "@/components/BookingModal";
import { serviceCategories, bookableServices } from "@/data/serviceCatalog";

export default function ServicesPage() {
  const [activeCategory, setActiveCategory] = useState(
    serviceCategories[0]?.id || ""
  );
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [selectedServiceId, setSelectedServiceId] = useState(null);

  const openBooking = (serviceId = null) => {
    setSelectedServiceId(serviceId);
    setIsBookingOpen(true);
  };

  const currentCategory =
    serviceCategories.find((category) => category.id === activeCategory) ||
    serviceCategories[0];
  const currentServices = bookableServices.filter(
    (service) => service.categoryId === currentCategory.id
  );

  return (
    <div className="bg-[#F0F0F0] min-h-screen">
      {/* Header */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center">
          <h1 className="script-heading text-3xl md:text-4xl mb-4">
            Our Services
          </h1>
          <p className="text-[#666] max-w-lg mx-auto">
            Select a category to explore packages and book your look.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <div className="glass-card p-4 md:p-5">
          <p className="text-[11px] uppercase tracking-[0.22em] text-[#888] text-center mb-3">
            Service Categories
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            {serviceCategories.map((category) => (
              <button
                key={category.id}
                onClick={() => {
                  setActiveCategory(category.id);
                }}
                className={`px-4 py-2.5 rounded-full text-sm transition-all border ${
                  activeCategory === category.id
                    ? "bg-[#111111] text-white border-[#111111]"
                    : "bg-white text-[#111111] border-[#e4e4e4] hover:bg-gray-100"
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section
        data-testid="services-section"
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16"
      >
        <div className="mb-5 text-center">
          <h2 className="section-heading text-2xl">{currentCategory.name}</h2>
          <p className="text-xs tracking-[0.14em] uppercase text-[#888] mt-1">
            {currentServices.length} Package{currentServices.length === 1 ? "" : "s"} Available
          </p>
        </div>
        {currentServices.length === 0 ? (
          <div className="glass-card p-8 text-center">
            <p className="text-[#666]">
              This category is ready. Add services here next.
            </p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-6">
            {currentServices.map((service, index) => (
              <div key={service.id} data-testid={`service-card-${index}`} className="luxury-card rounded-2xl p-6 flex flex-col">
                <div className="flex items-start justify-between gap-4 mb-3">
                  <h3 className="font-serif text-[1.38rem] leading-tight">{service.name}</h3>
                  <div className="shrink-0 text-right">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#888]">Starting</p>
                    <p className="text-lg font-medium text-[#111]">{service.price}</p>
                  </div>
                </div>
                <div className="inline-flex items-center gap-1 text-xs text-[#666] bg-[#f7f7f7] border border-[#ececec] rounded-full px-3 py-1.5 w-fit mb-4">
                  <Clock size={13} /> {service.duration}
                </div>

                {service.details && (
                  <div className="rounded-xl border border-[#ececec] bg-white px-4 py-3 mb-4">
                    <p className="text-sm text-[#555] whitespace-pre-line leading-relaxed">
                      {service.details}
                    </p>
                  </div>
                )}

                <div className="pt-3 mt-auto border-t border-[#ececec] flex items-center justify-between gap-3">
                  <span className="text-[11px] tracking-[0.14em] text-[#666] uppercase">
                    {service.cta || "Book this package"}
                  </span>
                  <button
                    onClick={() => openBooking(service.id)}
                    className="btn-black text-xs px-5 py-2.5 rounded-full"
                  >
                    Book
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Note */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="glass-card p-6 text-center">
          <p className="text-[#666] text-sm">
            <strong>Please Note:</strong> All prices are starting prices. Final pricing 
            may vary based on hair length, density, and specific styling requirements.
          </p>
        </div>
      </section>

      {/* Booking Modal */}
      <BookingModal
        isOpen={isBookingOpen}
        onClose={() => setIsBookingOpen(false)}
        preSelectedService={selectedServiceId}
      />
    </div>
  );
}
